import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { ICompany, ICompanyResponse } from '../../models/company.model';
import { HttpService } from 'src/app/core/http/http.service';
import { UnsubscribeOnDestroyAdapter } from 'src/app/core/unsuscribe-adapter/unsuscribe-adapter';
import { MatAutocompleteTrigger } from '@angular/material';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OrderStorageService } from 'src/app/main/orders/services/order-storage.service';
import { Router } from '@angular/router';
import { IResponseSalesList, ISales, IUser } from '@shared/models/login.model';
import { SessionStorageService } from 'src/app/main/orders/services/session-storage.service';
import { ALL_LABEL } from '@shared/globals/app-constants';
import { UserService } from '@shared/services/user.service';

export const ALL_COMPANIES: ICompany = {
  companies_id: -1,
  quotescount: null,
  orderscount: null,
  company_name: 'All',
  company_phone: null,
  company_email: null,
  company_photo: null,
  company_address: null,
  company_source_id: null,
  company_source_name: null,
  shipper_type: null,
  shipper_type_id: null,
  poc_name: null,
  poc_email: null,
  poc_phone: null,
  poc_photo: null,
  notes_quantity: null,
  gross_profit: null,
  id: null,
  name: null,
  phone: null,
  photo: null,
  email: null,
  status: null,
  deactivated_by: null,
  deactivated_at: null,
  userscount: null,
  address: null,
  fax: null,
  CompanyType: {
    type: null
  },
  GisCity: null,
  customerDetail: {
    company_id: null,
    show_carriers: null,
    shipper_type: null,
    operatorUser: null
  },
  users: []
};

export const ALL_SALES_REP: ISales = {
  id: -1,
  name: 'All',
  last_name: '',
  username: '',
  password: '',
  photo: null,
  phone: null,
  phone_ext: null,
  company_id: null,
  is_company_admin: false,
  is_super_admin: false,
  enabled: true,
  is_operator: false,
  succes_rate: null
};

@Component({
  selector: 'app-company-filter',
  templateUrl: './company-filter.component.html',
  styleUrls: ['./company-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyFilterComponent extends UnsubscribeOnDestroyAdapter
  implements OnInit, OnChanges {
  @ViewChild(MatAutocompleteTrigger, {
    read: MatAutocompleteTrigger,
    static: false
  })
  inputAutoComplete: MatAutocompleteTrigger;

  @Input() includeAll = false;
  @Input() activeNewCustomer = false;
  @Input() showSalesRepFilter = false;
  @Output() setCompany: EventEmitter<ICompany> = new EventEmitter<ICompany>();
  @Output() setSalesRep: EventEmitter<number> = new EventEmitter<number>();

  public ALL_LABEL = ALL_LABEL;
  public companies: ICompany[] = [];
  public companyName: string;
  public companyStore: ICompany;
  public companyFilterCtrl: FormControl = new FormControl('');
  public loadingCompanies = true;
  private searchSubject = new Subject<string>();
  public searchResult;
  public loadingResults;
  public user: IUser;

  // Sales Rep Filter properties
  public salesName: string;
  public salesFilterCtrl: FormControl = new FormControl();
  public loadingSales = false;
  public salesList: ISales[] = [];
  private salesRepSelected: ISales;
  private lastSalesValue: string = null;

  constructor(
    private service: HttpService,
    private storageService: OrderStorageService,
    private sessionStorage: SessionStorageService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private userService: UserService
  ) {
    super();
    this.user = this.userService.userInfo;
  }

  ngOnInit(): void {
    this.initialValidation();
    this.getListOfCompanies(true);
    this.setupSearchDebounce();
    if (
      this.router.url.includes('orders') ||
      this.router.url.includes('payments')
    )
      this.clearOrderCompanyFilter();
    
    // Initialize sales rep filter
    this.subscribeSalesChanges();
    this.filterSales();
    this.validateSalesFilterApply();
    this.clearFilterSales();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.includeAll &&
      changes.includeAll &&
      changes.includeAll.currentValue &&
      this.companies.indexOf(ALL_COMPANIES) === -1
    ) {
      this.companies.unshift(ALL_COMPANIES);
    }
  }

  public initialValidation(): void {
    this.companyStore = JSON.parse(localStorage.getItem('COMPANY_STORE'));
    this.onSelectCompany(this.companyStore);
  }

  private setupSearchDebounce(): void {
    this.subs.add(
      this.searchSubject
        .pipe(debounceTime(300))
        .subscribe((searchTerm: string) => {
          this.performCompanyFilter(searchTerm);
        })
    );
  }

  /**
   * Method to select a company, save into the storage and emmit its id
   * @method onSelectCompany
   * @param company ICompany
   */
  public onSelectCompany(company: ICompany): void {
    if (company) {
      this.companyStore = company;
      this.companyName = this.companyStore.company_name;
      localStorage.setItem('COMPANY_STORE', JSON.stringify(this.companyStore));
      this.setCompany.emit(company);
      this.validateCompanyStoreInList();
    }
  }

  /**
   * Method to handle input change and trigger debounced search
   * @method onInputChange
   */
  public onInputChange(): void {
    const value = this.companyFilterCtrl.value
      ? this.companyFilterCtrl.value.toLowerCase()
      : '';
    this.searchSubject.next(value);
  }

  /**
   * Method to filter the company list by a value with debounce
   * @method performCompanyFilter
   * @param searchTerm string
   */
  private performCompanyFilter(searchTerm: string): void {
    if (searchTerm.trim() !== '' && searchTerm.length > 2) {
      this.loadingCompanies = true;
      this.subs.add(
        this.service
          .get<ICompanyResponse>('company/customer/list', {
            filters: JSON.stringify({
              term: searchTerm,
              active: true,
              autocomplete: true,
              isLightVersion: true
            }),
            pagination: JSON.stringify({ page: 0, pageSize: 40 })
          })
          .subscribe(data => {
            this.companies = [...data.records];
            this.validateCompanyStoreInList();
            this.loadingCompanies = false;
            this.cdr.detectChanges();
          })
      );
    } else {
      this.getListOfCompanies(false);
    }
  }

  /**
   * Method to clean input of company
   * @method cleanInput
   */
  public cleanInput(evt): void {
    evt.stopPropagation();
    this.getListOfCompanies(false);
    this.inputAutoComplete.openPanel();
    this.companyName = null;
  }

  /**
   * Method to get company list
   * @method getListOfCompanies
   */
  private getListOfCompanies(initComponent: boolean): void {
    this.loadingCompanies = true;
    this.subs.add(
      this.service
        .get<ICompanyResponse>('company/customer/list', {
          filters: JSON.stringify({
            active: true,
            autocomplete: true,
            isLightVersion: true
          }),
          pagination: JSON.stringify({ page: 0, pageSize: 40 })
        })
        .subscribe(response => {
          this.companies = [...response.records];
          this.validateCompanyStoreInList();
          this.loadingCompanies = false;
          if (
            this.includeAll &&
            this.companies.filter(
              c => c.companies_id === ALL_COMPANIES.companies_id
            ).length === 0
          ) {
            this.companies.unshift(ALL_COMPANIES);
          } else {
            this.companies.splice(0, 0, this.companies.pop());
          }
          if (initComponent) {
            this.validateCompanyStore();
          }
        })
    );
  }

  /**
   * Method to validate if there is a company in the store
   * @method validateCompanyStore
   */
  private validateCompanyStore(): void {
    if (!this.companyStore) {
      this.companyStore = JSON.parse(localStorage.getItem('COMPANY_STORE'));
      this.onSelectCompany(
        this.companyStore
          ? this.companyStore.companies_id === -1 && !this.includeAll
            ? this.companies[0]
            : this.companyStore
          : this.companies[0]
      );
    }
  }

  private validateCompanyStoreInList(): void {
    if (this.companyStore && this.companies) {
      const find = this.companies.filter(
        company => company.companies_id === this.companyStore.companies_id
      );
      if (find.length === 0) {
        this.companies.push(this.companyStore);
      }
    }
  }

  private clearOrderCompanyFilter(): void {
    this.subs.add(
      this.storageService.filters.subscribe(filters => {
        const allCompanies = filters.some(
          f => f.filter_name === 'company' && f.value === -1
        );
        if (JSON.stringify(filters) === '[]' || allCompanies) {
          this.companyName = 'All';
          this.companyFilterCtrl.setValue(null);
          this.cdr.detectChanges();
        }
      })
    );
  }

  private subscribeSalesChanges(): void {
    this.subs.add(
      this.salesFilterCtrl.valueChanges
        .pipe(debounceTime(300))
        .subscribe(value => {
          if (value !== this.lastSalesValue) {
            this.lastSalesValue = value;
            this.filterSales();
          }
        })
    );
  }

  public filterSales(): void {
    const value = this.salesFilterCtrl.value
      ? this.salesFilterCtrl.value.toLowerCase()
      : '';
    const params =
      value.trim() !== '' && value.length > 2
        ? {
            term: value
          }
        : {};
    this.loadingSales = true;
    this.subs.add(
      this.service
        .get<IResponseSalesList>('user/list/sales_rep', {
          filters: JSON.stringify(params),
          pagination: JSON.stringify({ page: 0, pageSize: 40 })
        })
        .subscribe(
          data => {
            this.salesList = data.records;
            
            if (!this.salesList.some(s => s.id === -1)) {
              this.salesList.unshift(ALL_SALES_REP);
            }
            
            if (
              this.salesRepSelected &&
              !this.salesList.some(s => s.id === this.salesRepSelected.id)
            ) {
              this.salesList.push(this.salesRepSelected);
            }
            this.loadingSales = false;
            this.cdr.detectChanges();
          },
          error => {
            this.loadingSales = false;
            this.cdr.detectChanges();
          }
        )
    );
  }

  public onSelectSalesRep(sales: ISales): void {
    this.salesRepSelected = sales;
    this.salesName = `${sales.name} ${sales.last_name}`;
    
    if (sales.id === -1) {
      this.storageService.removeFilter({ filter_name: 'salesrep_id' });
    } else {
      this.storageService.addOrEditFilter({
        filter_name: 'salesrep_id',
        value: sales.id
      });
    }
    
    localStorage.setItem('SALES_STORE', JSON.stringify(sales));
    
    this.sessionStorage.setProperty('salesRep', sales.id, sales);
    this.sessionStorage.getHistoryNavigation();
    
    this.setSalesRep.emit(sales.id);
    
    this.cdr.detectChanges();
  }

  validateSalesFilterApply() {
    try {
      if (
        this.sessionStorage.historyNavigation &&
        this.sessionStorage.historyNavigation.salesRep &&
        this.sessionStorage.historyNavigation.salesRep.value
      ) {
        this.storageService.addOrEditFilter({
          filter_name: 'salesrep_id',
          value: this.sessionStorage.historyNavigation.salesRep.value.id
        });
        const salesValue = this.sessionStorage.historyNavigation.salesRep.value;
        this.salesName = salesValue ? salesValue.name : null;
        this.salesRepSelected = salesValue;
      }
    } catch (error) {
      console.log('Error applying sales filter', error);
    }
  }

  private clearFilterSales(): void {
    this.subs.add(
      this.storageService.filters.subscribe(filters => {
        if (filters.findIndex(f => f.filter_name === 'salesrep_id') < 0) {
          this.salesName = null;
          this.salesFilterCtrl.setValue(null);
          this.salesRepSelected = null;
          this.cdr.detectChanges();
        }
      })
    );
  }


  public clearCompanySelection(): void {
    this.onSelectCompany(ALL_COMPANIES);
    this.companyName = ALL_LABEL;
    this.companyFilterCtrl.setValue(null);
    this.cdr.detectChanges();
  }


  public clearSalesRepSelection(): void {
    this.onSelectSalesRep(ALL_SALES_REP);
    this.salesName = ALL_LABEL;
    this.salesFilterCtrl.setValue(null);
    this.cdr.detectChanges();
  }


  public clearAllSelections(): void {
    this.clearCompanySelection();
    this.clearSalesRepSelection();
  }
}
